import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Empty from 'src/components/Empty';
import ServiceRequestForm from 'src/components/ServiceRequestForm/ServiceRequestForm';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { ServiceRequestDashboardTable } from 'src/pages/ServiceRequests/components/ServiceRequestDashboardTable';
import { PAGINATION_KEYS } from 'src/store/slices/persistedUiSlice';
import { ServiceRequestDashboardActions } from 'src/pages/ServiceRequests/components/ServiceRequestDashboardActions';
import ServiceRequestDashboardToolbar from 'src/pages/ServiceRequests/components/ServiceRequestDashboardToolbar/ServiceRequestDashboardToolbar';
import { AssignedStatusOverlay } from 'src/pages/ServiceRequests/components/Overlays/BulkStatus/AssignedStatusOverlay';
import { GroupingOverlay } from 'src/pages/ServiceRequests/components/Overlays/GroupingOverlay';
import { DownloadCSVAndPDFParserOverlay } from 'src/utils/DownloadCSVAndPDFParserOverlay/DownloadCSVAndPDFParserOverlay';
import 'src/pages/ServiceRequests/ServiceRequestDashBoardTable.scss';
import PropertyServiceRequestsDeclineModal from './PropertyServiceRequestsDeclineModal';
import { UsePropertyServiceRequestsOrchestratorReturn } from './CustomHooks/PropertyServiceRequests/usePropertyServiceRequestsOrchestrator';

export interface PropertyServiceRequestsProps {
  property: any;
  orchestrator: UsePropertyServiceRequestsOrchestratorReturn;
  navigateTo: string;
  navLabel: string;
}

const PropertyServiceRequests = ({
  property,
  orchestrator,
  navigateTo,
  navLabel,
}: PropertyServiceRequestsProps) => {
  const propertyId: string = property?.id ?? '';

  const user: any = useSelector(selectAuthenticatedUser);
  const roleName: string = user?.roles?.[0]?.role?.name ?? '';

  const [isServiceRequestFormOpen, setIsServiceRequestFormOpen] =
    useState<boolean>(false);

  // Plumbed unchanged into the legacy ServiceRequestForm so the existing create-SR flow keeps working — see ServiceRequestFormSteps for fields read.
  const serviceRequestConfig = useMemo(
    () => ({
      property_id: propertyId,
      propertyId: propertyId,
    }),
    [propertyId]
  );

  const isInitialLoading: boolean =
    orchestrator.isLoading && orchestrator.serviceRequests.length === 0;

  const showEmpty: boolean =
    !orchestrator.isLoading && orchestrator.visibleServiceRequests.length === 0;

  const canCreateServiceRequest: boolean =
    roleName !== 'Vendor' &&
    roleName !== 'Service Provider' &&
    roleName !== 'Realtor';

  return (
    <div>
      {isServiceRequestFormOpen && (
        <ServiceRequestForm
          propertyDisabled={true}
          serviceRequest={serviceRequestConfig}
          from="Property"
          onClose={() => setIsServiceRequestFormOpen(false)}
        />
      )}

      <GroupingOverlay
        showGroupOverlay={orchestrator.isGroupingModalOpen}
        setShowGroupOverlay={(show: boolean) => {
          if (!show) {
            orchestrator.closeGroupingModal();
          }
        }}
        selectedServiceRequestIds={orchestrator.selectedIds}
        groupingPropertyId={propertyId}
        fetchDashboard={() => void orchestrator.refetch()}
        existingGroups={[]}
      />

      <PropertyServiceRequestsDeclineModal
        isOpen={orchestrator.isDeclineModalOpen}
        onClose={orchestrator.closeDeclineModal}
        onConfirm={(reason: string, reasonType: string) =>
          void orchestrator.bulkDecline(reason, reasonType)
        }
      />

      <AssignedStatusOverlay
        showAssignedStatusOverlay={orchestrator.isAssignOverlayOpen}
        setShowAssignedStatusOverlay={(show: boolean) => {
          if (!show) {
            orchestrator.closeAssignOverlay();
          }
        }}
        serviceRequests={orchestrator.selectedServiceRequests}
        fetchDashboard={() => void orchestrator.refetch()}
      />

      <DownloadCSVAndPDFParserOverlay
        isOpen={orchestrator.isDownloadOverlayOpen}
        onClose={orchestrator.closeDownloadOverlay}
        onDownloadCSV={orchestrator.onDownloadCSV}
        isCSVLoading={orchestrator.isCSVLoading}
        onDownloadPDF={orchestrator.onDownloadPDF}
        isPDFLoading={orchestrator.isPDFLoading}
      />

      <ServiceRequestDashboardToolbar
        canCreateServiceRequest={canCreateServiceRequest}
        onCreateServiceRequest={() => setIsServiceRequestFormOpen(true)}
      >
        <ServiceRequestDashboardActions
          searchString={orchestrator.searchQuery}
          onChangeSearchString={orchestrator.setSearchQuery}
          showResolved={orchestrator.showResolvedOnly}
          onShowResolved={orchestrator.toggleShowResolved}
          onShowColumnSelectionDialog={orchestrator.openColumnSelectionDialog}
          onBulkStatusUpdate={(status: string) =>
            void orchestrator.bulkUpdateStatus(status)
          }
          disableGroup={orchestrator.selectedIds.length === 0}
          onDownload={orchestrator.openDownloadOverlay}
          selectedCount={orchestrator.selectedIds.length}
          hideGroupButton={true}
        />
      </ServiceRequestDashboardToolbar>

      {showEmpty ? (
        <Empty
          direction="ttb"
          headline="No service requests for this property"
          strapline="Submitted service requests for this property will be listed here."
          image={null}
          searchIsActive={orchestrator.searchQuery !== ''}
        />
      ) : (
        <ServiceRequestDashboardTable
          serviceRequests={orchestrator.visibleServiceRequests}
          selectedServiceRequests={orchestrator.selectedIds}
          onSelectServiceRequest={orchestrator.toggleSelection}
          onSelectGroup={orchestrator.toggleSelectionBatch}
          onSelectAll={orchestrator.toggleSelectAllVisible}
          allSelected={orchestrator.allVisibleSelected}
          onDeleteGroup={(groupId: string) =>
            void orchestrator.deleteGroup(groupId)
          }
          onRemoveFromGroup={(serviceRequestId: string) =>
            void orchestrator.removeFromGroup(serviceRequestId)
          }
          navigateTo={navigateTo}
          navLabel={navLabel}
          persistKey={`${PAGINATION_KEYS.propertyServiceRequests}-${property?.id}`}
          loading={isInitialLoading}
          showColumnSelectionDialog={orchestrator.showColumnSelectionDialog}
          onCloseColumnSelectionDialog={orchestrator.closeColumnSelectionDialog}
        />
      )}
    </div>
  );
};

export default PropertyServiceRequests;
